import { DesignSystemAlertStacking } from '../../theme/design-system/types';
import {
    alertGridAreaActions,
    alertGridAreaClose,
    alertGridAreaContent,
    alertGridAreaIcon,
    alertGridAreaTitle,
} from './constants';
import { AlertComponent } from './Alert.types';

/**
 * Builds the appropriate CSS grid template based on registered Alert components.
 * Dynamically generates grid layout instead of using static template permutations.
 *
 * @param registeredComponents - Set of components that have registered themselves
 * @param stacking - Layout orientation (inline or stacked)
 * @returns CSS grid template string
 */
export const buildAlertGrid = (
    registeredComponents: Set<AlertComponent>,
    stacking: DesignSystemAlertStacking,
): string => {
    const hasTitle = registeredComponents.has('title');
    const hasActions = registeredComponents.has('actions');
    const hasClose = registeredComponents.has('close');

    if (stacking === 'inline') {
        return buildInlineGrid(hasTitle, hasActions, hasClose);
    } else {
        return buildStackedGrid(hasTitle, hasActions, hasClose);
    }
};

/**
 * Builds inline grid template with all components in a single row.
 * Layout: [icon] [title?] [content] [actions?] [close?]
 */
const buildInlineGrid = (
    hasTitle: boolean,
    hasActions: boolean,
    hasClose: boolean,
): string => {
    const areas: string[] = [alertGridAreaIcon];
    const columns: string[] = ['auto'];

    if (hasTitle) {
        areas.push(alertGridAreaTitle);
        columns.push('auto');
    }

    areas.push(alertGridAreaContent);
    columns.push('1fr');

    if (hasActions) {
        areas.push(alertGridAreaActions);
        columns.push('auto');
    }

    if (hasClose) {
        areas.push(alertGridAreaClose);
        columns.push('auto');
    }

    const areaTemplate = `"${areas.join(' ')}" auto`;
    const columnTemplate = columns.join(' ');

    return `${areaTemplate} / ${columnTemplate}`;
};

/**
 * Builds stacked grid template with components in multiple rows.
 * Layout: Row 1: [icon] [title?] [close?]
 *         Row 2: [icon] [content] [close?]
 *         Row 3: [icon] [actions?] [close?]
 */
const buildStackedGrid = (
    hasTitle: boolean,
    hasActions: boolean,
    hasClose: boolean,
): string => {
    const lines: string[] = [];
    if (hasTitle) {
        const titleAreas = [alertGridAreaIcon, alertGridAreaTitle];
        if (hasClose) {
            titleAreas.push(alertGridAreaClose);
        }
        lines.push(`"${titleAreas.join(' ')}" auto`);
    }

    const contentAreas = [alertGridAreaIcon, alertGridAreaContent];
    if (hasClose && !hasTitle) {
        contentAreas.push(alertGridAreaClose);
    } else if (hasClose) {
        contentAreas.push(alertGridAreaClose);
    }
    lines.push(`"${contentAreas.join(' ')}" auto`);

    if (hasActions) {
        const actionAreas = [alertGridAreaIcon, alertGridAreaActions];
        if (hasClose) {
            actionAreas.push(alertGridAreaClose);
        }
        lines.push(`"${actionAreas.join(' ')}" auto`);
    }

    const columns = hasClose ? 'auto 1fr auto' : 'auto 1fr';

    return `${lines.join('\n    ')} / ${columns}`;
};
